import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Minus, Plus, ShoppingCart } from 'lucide-react';
import type { DirectoryOrgDetail, PublicDoctor, PublicLabTest, PublicProduct } from '@his/shared';
import { publicGet, publicPost } from '@/lib/public-api';
import { qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { money } from '@/lib/utils';
import { Dialog, EmptyState, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, FormSkeleton, Input, Spinner, TableSkeleton } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { useToast } from '@/components/ui/toast';
import { usePublicAuth } from './PublicLayout';
import { usePrefill } from './use-prefill';
import { cartTotal, readCartDraft, writeCartDraft, type CartLine } from './cart-draft';

/**
 * One branch: the doctors it offers, the tests it runs, the medicines it has.
 *
 * Everything here ends in a *request*. The page says so plainly, because a
 * patient who thinks they hold a confirmed appointment and turns up to find
 * they do not is the worst outcome this feature can produce.
 */
export function BranchPage() {
  const { slug = '', branchId = '' } = useParams();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') ?? 'doctors');

  const org = useQuery({
    queryKey: ['public-org', slug],
    queryFn: () => publicGet<DirectoryOrgDetail>(`/facilities/${slug}`),
  });

  const branch = org.data?.branches.find((candidate) => candidate.branchId === branchId);

  if (org.isLoading) {
    return (
      <TableSkeleton />
    );
  }
  if (!branch) return <EmptyState title="Not found" description="This branch is no longer listed." />;

  const tabs = [
    branch.types.includes('hospital') ? { id: 'doctors', label: 'See a doctor' } : null,
    branch.types.includes('lab') ? { id: 'tests', label: 'Book lab tests' } : null,
    branch.types.includes('pharmacy') ? { id: 'pharmacy', label: 'Order medicines' } : null,
  ].filter(Boolean) as { id: string; label: string }[];

  const active = tabs.some((candidate) => candidate.id === tab) ? tab : (tabs[0]?.id ?? 'doctors');

  return (
    <>
      <Link
        to={`/find/${slug}`}
        className="mb-4 inline-flex items-center gap-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground focus-ring"
      >
        <ArrowLeft className="h-4 w-4" /> {org.data?.name}
      </Link>

      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">{branch.branchName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {[branch.address, branch.city].filter(Boolean).join(', ')}
        </p>
      </div>

      {!branch.acceptsOnlineRequests && (
        <Alert className="mb-4">
          This hospital takes bookings at its own desk. You can see its doctors, tests and
          medicines here — to book or order, call the branch
          {branch.phone ? ` on ${branch.phone}` : ''}.
        </Alert>
      )}

      {tabs.length === 0 ? (
        <EmptyState title="Nothing bookable here" description="This branch has not published any services." />
      ) : (
        <>
          <Tabs tabs={tabs} active={active} onChange={setTab} />
          {active === 'doctors' && <DoctorsTab slug={slug} branchId={branchId} readOnly={!branch.acceptsOnlineRequests} />}
          {active === 'tests' && <TestsTab slug={slug} branchId={branchId} readOnly={!branch.acceptsOnlineRequests} />}
          {active === 'pharmacy' && <PharmacyTab slug={slug} branchId={branchId} readOnly={!branch.acceptsOnlineRequests} />}
        </>
      )}
    </>
  );
}

// ── See a doctor ──────────────────────────────────────────────────────────────

function DoctorsTab({ slug, branchId, readOnly }: { slug: string; branchId: string; readOnly: boolean }) {
  const navigate = useNavigate();

  const doctors = useQuery({
    queryKey: ['public-doctors', slug, branchId],
    queryFn: () => publicGet<PublicDoctor[]>(`/facilities/${slug}/branches/${branchId}/doctors`),
  });

  if (doctors.isLoading) return <FormSkeleton />;
  if (!doctors.data || doctors.data.length === 0) {
    return <EmptyState title="No doctors listed" description="This branch has not published its doctors." />;
  }

  return (
    <>
      <div className="grid gap-2 sm:grid-cols-2">
        {doctors.data.map((doctor) => (
          <div key={doctor.id} className="flex items-center justify-between gap-3 rounded-lg border bg-card p-4">
            <div>
              <p className="font-medium">{doctor.name}</p>
              <p className="text-xs text-muted-foreground">
                {[doctor.designation, doctor.specialty].filter(Boolean).join(' · ') || 'Doctor'}
              </p>
            </div>
            {!readOnly && (
              <Button size="sm" onClick={() => navigate(`/find/${slug}/${branchId}/book${qs({ doctorId: doctor.id })}`)}>
                Request
              </Button>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// ── Lab tests ─────────────────────────────────────────────────────────────────

function TestsTab({ slug, branchId, readOnly }: { slug: string; branchId: string; readOnly: boolean }) {
  const toast = useToast();
  const { account, requireSignIn } = usePublicAuth();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<PublicLabTest[]>([]);
  const [open, setOpen] = useState(false);
  const [patientName, setPatientName] = useState(account?.name ?? '');
  const [phone, setPhone] = useState(account?.phone ?? '');
  const [error, setError] = useState<string | null>(null);

  usePrefill(patientName, setPatientName, account?.name);
  usePrefill(phone, setPhone, account?.phone);

  const tests = useQuery({
    queryKey: ['public-tests', slug, branchId, search],
    queryFn: () => publicGet<PublicLabTest[]>(`/facilities/${slug}/branches/${branchId}/tests${qs({ q: search })}`),
  });

  const total = picked.reduce((sum, test) => sum + test.priceMinor, 0);

  const send = useMutation({
    mutationFn: () =>
      publicPost('/requests', {
        tenantSlug: slug,
        branchId,
        kind: 'lab',
        patientName,
        phone,
        testIds: picked.map((test) => test.id),
      }),
    onSuccess: () => {
      toast.success('Request sent', 'The lab will confirm your visit.');
      setPicked([]);
      setOpen(false);
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search tests, e.g. CBC"
        className="mb-3"
      />

      {picked.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border bg-card p-3">
          {picked.map((test) => (
            <Badge key={test.id} variant="secondary">
              {test.name}
              <button
                type="button"
                className="ms-1.5 text-muted-foreground hover:text-foreground"
                onClick={() => setPicked((current) => current.filter((item) => item.id !== test.id))}
                aria-label={`Remove ${test.name}`}
              >
                ×
              </button>
            </Badge>
          ))}
          <span className="ms-auto text-sm font-medium tabular">{money(total)}</span>
          <Button size="sm" onClick={() => setOpen(true)}>
            Request {picked.length} test{picked.length > 1 ? 's' : ''}
          </Button>
        </div>
      )}

      {tests.isLoading ? (
        <Spinner />
      ) : tests.data && tests.data.length > 0 ? (
        <div className="divide-y rounded-lg border">
          {tests.data.map((test) => {
            const chosen = picked.some((item) => item.id === test.id);
            return (
              <div key={test.id} className="flex items-center justify-between gap-3 p-3">
                <div>
                  <p className="text-sm font-medium">{test.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {test.departmentGroup} · report in about {Math.round(test.tatMinutes / 60)}h
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm tabular">{money(test.priceMinor)}</span>
                  {!readOnly && (
                    <Button
                      size="sm"
                      variant={chosen ? 'outline' : 'default'}
                      onClick={() =>
                        setPicked((current) =>
                          chosen ? current.filter((item) => item.id !== test.id) : [...current, test],
                        )
                      }
                    >
                      {chosen ? 'Remove' : 'Add'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState title="No tests found" />
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Request these tests"
        description="The lab confirms your visit and tells you what to bring"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            {account ? (
              <Button
                onClick={() => {
                  setError(null);
                  send.mutate();
                }}
                loading={send.isPending}
                disabled={!patientName.trim() || !phone.trim()}
              >
                Send request
              </Button>
            ) : (
              <Button onClick={requireSignIn}>Sign in to continue</Button>
            )}
          </>
        }
      >
        <div className="space-y-4">
          {error && <Alert variant="destructive">{error}</Alert>}
          <ul className="rounded-md border p-3 text-sm">
            {picked.map((test) => (
              <li key={test.id} className="flex justify-between py-0.5">
                <span>{test.name}</span>
                <span className="tabular">{money(test.priceMinor)}</span>
              </li>
            ))}
            <li className="mt-1 flex justify-between border-t pt-1 font-medium">
              <span>Total</span>
              <span className="tabular">{money(total)}</span>
            </li>
          </ul>
          <Field label="Patient name" required>
            <Input value={patientName} onChange={(e) => setPatientName(e.target.value)} />
          </Field>
          <Field label="Phone" required>
            <PhoneText value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
          </Field>
        </div>
      </Dialog>
    </>
  );
}

// ── Pharmacy ──────────────────────────────────────────────────────────────────

function PharmacyTab({ slug, branchId, readOnly }: { slug: string; branchId: string; readOnly: boolean }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<CartLine[]>(() => readCartDraft(slug, branchId));

  useEffect(() => {
    writeCartDraft(slug, branchId, lines);
  }, [slug, branchId, lines]);

  const products = useQuery({
    queryKey: ['public-products', slug, branchId, search],
    queryFn: () => publicGet<PublicProduct[]>(`/facilities/${slug}/branches/${branchId}/products${qs({ q: search })}`),
  });

  const total = cartTotal(lines);
  const qtyOf = (productId: string) => lines.find((line) => line.product.id === productId)?.qty ?? 0;

  const setQty = (product: PublicProduct, qty: number) =>
    setLines((current) => {
      const others = current.filter((line) => line.product.id !== product.id);
      if (qty <= 0) return others;
      const existing = current.find((line) => line.product.id === product.id);
      return existing ? current.map((line) => (line.product.id === product.id ? { ...line, qty } : line)) : [...others, { product, qty }];
    });

  return (
    <>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search medicines, e.g. panadol"
        className="mb-3"
      />

      {lines.length > 0 && (
        <div className="sticky top-16 z-30 mb-3 flex items-center gap-3 rounded-lg border bg-card p-3 shadow-sm">
          <ShoppingCart className="h-4 w-4 text-primary" />
          <span className="text-sm">
            {lines.length} item{lines.length > 1 ? 's' : ''}
          </span>
          <span className="ms-auto font-medium tabular">{money(total)}</span>
          <Button size="sm" onClick={() => navigate(`/find/${slug}/${branchId}/checkout`)}>
            Checkout
          </Button>
        </div>
      )}

      {products.isLoading ? (
        <Spinner />
      ) : products.data && products.data.length > 0 ? (
        <div className="divide-y rounded-lg border">
          {products.data.map((product) => (
            <div key={product.id} className="flex items-center justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{product.name}</p>
                <p className="text-xs text-muted-foreground">
                  {[product.strength, product.form, `pack of ${product.packSize}`].filter(Boolean).join(' · ')}
                </p>
                {product.isRxRequired && (
                  <Badge variant="warning" className="mt-1">
                    prescription needed
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm tabular">{money(product.priceMinor)}</span>
                {qtyOf(product.id) > 0 ? (
                  <div className="flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="outline"
                      aria-label="One fewer"
                      onClick={() => setQty(product, qtyOf(product.id) - 1)}
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </Button>
                    <span className="w-7 text-center text-sm tabular">{qtyOf(product.id)}</span>
                    <Button
                      size="icon"
                      variant="outline"
                      aria-label="One more"
                      onClick={() => setQty(product, qtyOf(product.id) + 1)}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : readOnly ? null : (
                  <Button size="sm" onClick={() => setQty(product, 1)}>
                    Add
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          title="Nothing in stock"
          description="This pharmacy has not published any medicines, or nothing is on the shelf right now."
        />
      )}
    </>
  );
}

